/**
 * 붙여넣기 파서 (P6-①) — 클립보드 HTML/텍스트 → BlockNote 블록 배열.
 * 샘플 block-editor.html의 blocksFromHTML/gridFromHTML 패턴을 우리 블록/직렬화에 이식.
 * 인라인은 inline.js(htmlToInlineContent)로 매핑(strong/em/u/s/code/a 보존).
 */

import { htmlToInlineContent } from "../serialize/inline.js";
import { normalizeExcelTableEl } from "./excel-format.js";
import { fitWidths } from "../table/fit.js";

const P = () => ({ textColor: "default", backgroundColor: "default", textAlignment: "left" });
const SKIP = new Set(["STYLE", "SCRIPT", "META", "LINK", "TITLE", "HEAD", "NOSCRIPT"]);
// 붙여넣기 경로: 소스 정렬용 개행을 공백으로 접어 이중 줄바꿈/줄분리 방지(collapseWhitespace).
const inlineOf = (el) => htmlToInlineContent(el, { collapseWhitespace: true });

/** <table> 요소 → BlockNote tableContent (colspan/rowspan/정렬/<th> 머리행 반영) */
function tableContentFromTableEl(t) {
  const rows = [...t.rows];
  if (!rows.length) return null;
  const out = rows.map(() => ({ cells: [] }));
  const occ = {};
  let maxCols = 0;
  let hasTh = false;
  rows.forEach((tr, r) => {
    let c = 0;
    [...tr.cells].forEach((cell) => {
      while (occ[r + "_" + c]) c++;
      const rs = cell.rowSpan || 1;
      const cs = cell.colSpan || 1;
      const props = { colspan: cs, rowspan: rs };
      const ta = cell.getAttribute("data-text-alignment") ||
        (String((cell.style && cell.style.textAlign) || cell.getAttribute("align") || "").toLowerCase().match(/left|center|right/) || [])[0];
      if (ta && /^(left|center|right)$/.test(ta)) props.textAlignment = ta;
      const bg = cell.getAttribute("data-background-color");
      if (bg) props.backgroundColor = bg;
      const tcol = cell.getAttribute("data-text-color");
      if (tcol) props.textColor = tcol;
      const va = cell.getAttribute("data-vertical-alignment");
      if (va) props.verticalAlignment = va;
      if (cell.tagName === "TH") hasTh = true;
      out[r].cells.push({ type: "tableCell", props, content: inlineOf(cell) });
      for (let dr = 0; dr < rs; dr++) for (let dc = 0; dc < cs; dc++) if (!(dr === 0 && dc === 0)) occ[(r + dr) + "_" + (c + dc)] = true;
      c += cs;
      maxCols = Math.max(maxCols, c);
    });
  });
  const content = { type: "tableContent", columnWidths: readSourceColWidths(t, maxCols), rows: out };
  // 머리행: 첫 행의 모든 셀이 <th>. 머리열: 모든 행의 첫 셀이 <th>. (둘 다면 둘 다 설정)
  // 이전엔 "첫 행에 th가 하나라도" → headerRows 로 판정해, th가 첫 '열'인 표(행머리)를 행머리로 오판했다.
  if (hasTh) {
    const firstRow = rows[0] ? [...rows[0].cells] : [];
    if (firstRow.length && firstRow.every((x) => x.tagName === "TH")) content.headerRows = 1;
    if (rows.length && rows.every((tr) => tr.cells[0] && tr.cells[0].tagName === "TH")) content.headerCols = 1;
  }
  return content;
}

function pxOf(v) {
  if (!v) return null;
  const m = String(v).trim().match(/^([\d.]+)\s*(px|pt)?$/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  return (m[2] || "px").toLowerCase() === "pt" ? Math.round((n * 96) / 72) : Math.round(n);
}
/** 소스 표의 열 폭(colgroup/첫 행 셀) 추출 — 없으면 null 배열 */
function readSourceColWidths(t, maxCols) {
  const widths = new Array(maxCols).fill(null);
  const cols = t.querySelectorAll(":scope > colgroup > col");
  if (cols.length) {
    let i = 0;
    cols.forEach((col) => {
      const span = parseInt(col.getAttribute("span") || "1", 10) || 1;
      const w = pxOf((col.style && col.style.width) || col.getAttribute("width"));
      for (let s = 0; s < span && i < maxCols; s++, i++) if (w) widths[i] = w;
    });
    if (widths.some(Boolean)) return widths;
  }
  const firstRow = t.rows && t.rows[0];
  if (firstRow) {
    let i = 0;
    [...firstRow.cells].forEach((cell) => {
      const cs = cell.colSpan || 1;
      const w = pxOf((cell.style && cell.style.width) || cell.getAttribute("width"));
      const per = w ? Math.round(w / cs) : null;
      for (let s = 0; s < cs && i < maxCols; s++, i++) if (per) widths[i] = per;
    });
  }
  return widths.some(Boolean) ? widths : new Array(maxCols).fill(null);
}

/** 넓은 표 폭 맞춤: 열폭 합이 maxWidth를 넘으면 축소(Σ≤maxWidth 보장, 각 ≥ effMin). 공용 solver 위임. */
export function fitColumnWidths(widths, maxWidth, min) {
  return fitWidths(widths, maxWidth, min || 40).widths;
}

/** 클립보드 HTML → 블록 배열 */
export function parseHtmlToBlocks(html) {
  if (typeof DOMParser === "undefined") return [];
  let doc;
  try { doc = new DOMParser().parseFromString(html, "text/html"); } catch (_) { return []; }
  const out = [];
  // <img> → image 블록(원격/Data URL 직접 참조). javascript:/SVG data URL은 차단(XSS).
  const imageBlockFromImg = (node) => {
    const src = (node.getAttribute("src") || "").trim();
    if (!src || /^javascript:/i.test(src) || /^data:image\/svg/i.test(src)) return null;
    const props = { url: src };
    const alt = node.getAttribute("alt");
    if (alt) props.caption = alt;
    const w = parseInt(node.getAttribute("width"), 10);
    if (w) props.previewWidth = w;
    return { type: "image", props, children: [] }; // blocknote 파일형 블록: content 키 없음
  };
  const pushPara = (node) => { const inline = inlineOf(node); if (inline.length) out.push({ type: "paragraph", props: P(), content: inline, children: [] }); };
  const pushTyped = (node, type, extra) => { const inline = inlineOf(node); if (inline.length) out.push({ type, props: { ...P(), ...(extra || {}) }, content: inline, children: [] }); };

  const walk = (cont) => {
    cont.childNodes.forEach((node) => {
      if (node.nodeType === 3) {
        const tx = node.nodeValue;
        if (tx && tx.trim()) out.push({ type: "paragraph", props: P(), content: [{ type: "text", text: tx, styles: {} }], children: [] });
        return;
      }
      if (node.nodeType !== 1) return;
      const t = node.tagName;
      if (SKIP.has(t)) return;
      if (t === "TABLE") { normalizeExcelTableEl(node); const c = tableContentFromTableEl(node); if (c) out.push({ type: "table", props: {}, content: c, children: [] }); return; }
      if (/^H[1-6]$/.test(t)) { pushTyped(node, "heading", { level: +t[1] }); return; }
      if (t === "LI") { pushTyped(node, node.closest("ol") ? "numberedListItem" : "bulletListItem"); return; }
      if (t === "UL" || t === "OL") { walk(node); return; }
      if (t === "BLOCKQUOTE") { pushTyped(node, "quote"); return; }
      if (t === "PRE") { const code = node.textContent || ""; out.push({ type: "codeBlock", props: { language: "" }, content: code ? [{ type: "text", text: code, styles: {} }] : [], children: [] }); return; }
      if (t === "IMG") { const b = imageBlockFromImg(node); if (b) out.push(b); return; } // 이미지 복사-붙여넣기/DnD
      if (t === "FIGURE" || t === "PICTURE") { walk(node); return; } // <figure><img><figcaption>, <picture><img>
      if (t === "P" || t === "DIV" || t === "SECTION" || t === "ARTICLE" || t === "BODY") {
        if (node.querySelector("table,ul,ol,h1,h2,h3,h4,h5,h6,li,blockquote,pre,img,figure,picture")) walk(node);
        else pushPara(node);
        return;
      }
      if (t === "BR") return;
      // 미분류 래퍼라도 이미지/표가 들어 있으면 재귀(텍스트만 추출되어 유실되지 않도록)
      if (node.querySelector && node.querySelector("img,figure,picture,table")) { walk(node); return; }
      pushPara(node);
    });
  };
  walk(doc.body);
  return out;
}

/** 여러 줄 텍스트 → 문단 블록들 */
export function blocksFromText(text) {
  return String(text || "")
    .split(/\r?\n/)
    .filter((l) => l.trim().length)
    .map((l) => ({ type: "paragraph", props: P(), content: [{ type: "text", text: l, styles: {} }], children: [] }));
}

/** 단일 URL 여부(공백 없는 http(s)) */
export function isBareUrl(text) {
  const t = String(text || "").trim();
  return /^https?:\/\/\S+$/.test(t) && !/\s/.test(t);
}

/** 텍스트에 http(s) URL이 포함되어 있는지 */
export function hasUrl(text) {
  return /https?:\/\/\S/.test(String(text || ""));
}

/**
 * 텍스트를 인라인 콘텐츠(text/link 노드 배열)로 변환 — http(s) URL 부분만 link로.
 * URL 뒤 흔한 문장부호(.,);]}>)는 링크에서 제외해 본문에 남긴다.
 */
export function linkifyToInline(text) {
  const s = String(text || "");
  const re = /https?:\/\/[^\s]+/g;
  const out = [];
  let last = 0, m;
  const pushText = (t) => { if (t) out.push({ type: "text", text: t, styles: {} }); };
  while ((m = re.exec(s))) {
    let url = m[0];
    let trail = "";
    const tm = url.match(/[.,);\]}>]+$/);
    if (tm) { trail = tm[0]; url = url.slice(0, -trail.length); }
    if (!url) { re.lastIndex = m.index + m[0].length; continue; }
    pushText(s.slice(last, m.index));
    out.push({ type: "link", href: url, content: [{ type: "text", text: url, styles: {} }] });
    if (trail) pushText(trail);
    last = m.index + m[0].length;
  }
  pushText(s.slice(last));
  return out;
}
